import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { CartProvider } from "@/lib/cart";
import { AdminAuthProvider, useAdminAuth } from "@/lib/auth";
import { CustomerAuthProvider } from "@/lib/customerAuth";

import Home from "@/pages/Home";
import Category from "@/pages/Category";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Product from "@/pages/Product";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerRegister from "@/pages/CustomerRegister";
import CustomerAccount from "@/pages/CustomerAccount";

import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminBulkImport from "@/pages/admin/BulkImport";
import AdminOrders from "@/pages/admin/Orders";
import AdminLogistics from "@/pages/admin/Logistics";
import AdminAnnouncements from "@/pages/admin/Announcements";
import AdminInvoice from "@/pages/admin/Invoice";
import AdminAwbLabel from "@/pages/admin/AwbLabel";

import StoreLayout from "@/components/StoreLayout";
import ScrollToTop from "@/components/ScrollToTop";

function ProtectedAdmin({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-ink-soft tracking-widest text-sm uppercase">Loading…</div>
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <CustomerAuthProvider>
            <CartProvider>
              <ScrollToTop />
              <Routes>
                {/* Storefront */}
                <Route element={<StoreLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/category/:slug" element={<Category />} />
                  <Route path="/product/:id" element={<Product />} />
                  <Route path="/order/:id" element={<OrderConfirmation />} />
                  <Route path="/login" element={<CustomerLogin />} />
                  <Route path="/register" element={<CustomerRegister />} />
                  <Route path="/account" element={<CustomerAccount />} />
                </Route>

                {/* Checkout — own minimal layout (Shopify-style) */}
                <Route path="/checkout" element={<Checkout />} />

                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdmin>
                      <AdminLayout />
                    </ProtectedAdmin>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="import" element={<AdminBulkImport />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="orders/:id/invoice" element={<AdminInvoice />} />
                  <Route path="orders/:id/awb" element={<AdminAwbLabel />} />
                  <Route path="logistics" element={<AdminLogistics />} />
                  <Route path="announcements" element={<AdminAnnouncements />} />
                </Route>
              </Routes>
              <Toaster position="top-right" richColors />
            </CartProvider>
          </CustomerAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
